"use client";

import React, { useState } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import Link from "next/link";
import { getImageUrl } from "@/utils/utils";
import RichContent from "@/components/ui/RichContent";

interface SustainabilityPageClientProps {
  sustainabilityPageData: any;
}

const SustainabilityPageClient: React.FC<SustainabilityPageClientProps> = ({
  sustainabilityPageData,
}) => {
  const [sustainabilityPolicyOpen, setSustainabilityPolicyOpen] =
    useState(false);
  const [managementFrameworkOpen, setManagementFrameworkOpen] = useState(false);

  // Animation variants for sections
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 60,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
      },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 30,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  // Action plan items will be populated from API data
  const actionPlanItems =
    sustainabilityPageData?.sustainability?.actionPlan?.cards || [];

  return (
    <>
      {/* Main Content Section */}
      <motion.section
        className="relative w-full"
        initial="hidden"
        animate="visible"
        variants={sectionVariants}
      >
        {/* Main Content Container */}
        <div className="flex flex-col items-center gap-[50px] pt-12 lg:pt-[100px]">
          {/* Section Header */}
          <div className="flex flex-col items-center gap-4">
            <h1 className="text-[#0A1119] text-center font-inter text-3xl lg:text-[40px] font-medium leading-tight lg:leading-[58px] tracking-[-1.44px]">
              {sustainabilityPageData?.sustainability?.title}
            </h1>
            <div className="w-[50px] h-[2px] bg-[#1A5852]"></div>
          </div>

          {/* Main Content with Background */}
          <div className="flex flex-col items-start gap-4 w-full relative">
            <div
              className="w-full shadow-[0_30px_60px_0_rgba(0,0,0,0.08)] bg-white relative overflow-hidden bg-cover bg-center bg-no-repeat group"
              style={{
                backgroundImage:
                  "url(/images/sustainability/sustainability-section1-bg.svg)",
              }}
            >
              {/* Background Pattern */}
              <Image
                src="/images/shared/page-blend-bg.svg"
                alt="Background pattern"
                fill
                className="object-cover opacity-30 luminosity mix-blend-luminosity pointer-events-none select-none"
              />

              {/* Content */}
              <div className="relative z-10 p-8 lg:p-[50px]">
                <div className="flex flex-col gap-4 mx-auto">
                  {/* Project Header */}
                  <div className="flex items-center gap-2 pb-2 border-b border-[#eceded]">
                    <h2 className="text-[#1A1D21] font-inter text-xl font-medium leading-7 tracking-[-0.72px]">
                      {
                        sustainabilityPageData?.sustainability?.actionPlan
                          ?.title
                      }
                    </h2>
                  </div>

                  {/* Content Container */}
                  <div className="flex flex-col gap-[50px]">
                    {/* ActionPlan Description */}
                    <RichContent
                      html={
                        sustainabilityPageData?.sustainability?.actionPlan
                          ?.description
                      }
                      className="mt-[-18px] text-[#3D434C] text-justify font-inter text-base leading-7"
                    />

                    {/* Action Plan Items Grid */}
                    <motion.div
                      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8"
                      variants={staggerContainer}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, amount: 0.1 }}
                    >
                      {actionPlanItems.map((item, index) => (
                        <motion.div
                          key={item.id || index + 1}
                          className="flex flex-col items-center w-full group/card"
                          variants={cardVariants}
                        >
                          {/* Number Circle */}
                          <div className="flex w-[120px] h-[120px] flex-col justify-center items-center relative z-10 mb-[-60px]">
                            <div
                              className="absolute w-[120px] h-[120px] rounded-full bg-[#E8E8E8]"
                              style={{ clipPath: "inset(50% 0 0 0)" }}
                            ></div>
                            <div className="flex w-[100px] h-[100px] flex-col justify-center items-center rounded-full bg-[#1c8579] relative z-10">
                              <span className="text-white text-center font-inter text-[32px] font-medium leading-[34px] tracking-[-0.72px]">
                                {index + 1}
                              </span>
                            </div>
                          </div>

                          {/* Description Card */}
                          <div className="flex h-[240px] md:h-[280px] px-6 pt-[70px] pb-6 items-center bg-white rounded-lg shadow-[0_30px_60px_0_rgba(0,0,0,0.08)] w-full cursor-pointer transition-shadow duration-300 group-hover/card:shadow-[0_40px_80px_0_rgba(0,0,0,0.25)]">
                            <div className="flex flex-col justify-center text-[#3D434C] text-center font-inter text-lg font-medium leading-7 tracking-[-0.72px]">
                              {item.text}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>

                    {/* NKB Sustainability Promotion Section */}
                    <div className="flex flex-col gap-4">
                      {/* Section Header */}
                      <div className="flex items-center gap-2 pb-2 border-b border-[#eceded]">
                        <h2 className="text-[#1A1D21] font-inter text-xl font-medium leading-7 tracking-[-0.72px]">
                          {
                            sustainabilityPageData?.sustainability?.promotion
                              ?.title
                          }
                        </h2>
                      </div>

                      {/* promotion description */}
                      <RichContent
                        html={
                          sustainabilityPageData?.sustainability?.promotion
                            ?.description
                        }
                        className="mt-[-18px] text-[#3D434C] text-justify font-inter text-base leading-7"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="flex h-[2px] bg-[#F4F5F6] relative overflow-hidden">
                <div className="h-[2px] bg-[#1C8579] absolute left-0 top-0 w-0 group-hover:w-full transition-all duration-700 ease-in-out" />
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Accordion Sections */}
      <motion.section
        className="bg-[#F4F5F6] pt-4"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={sectionVariants}
      >
        <div className="mx-auto">
          {/* Sustainability Fundamental Policy Accordion */}
          <div className="bg-white shadow-[0_30px_60px_0_rgba(0,0,0,0.08)] mb-4 relative overflow-hidden group">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-30 mix-blend-luminosity pointer-events-none">
              <svg
                width="1317"
                height="132"
                viewBox="0 0 1317 132"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full h-full object-cover"
              >
                <g
                  style={{ mixBlendMode: "luminosity" }}
                  opacity="0.3"
                  clipPath="url(#clip0_3744_904)"
                >
                  <path d="M3303.5 -668H0.5V800H3303.5V-668Z" fill="#F0F0F0" />
                  <defs>
                    <clipPath id="clip0_3744_904">
                      <rect
                        width="3303"
                        height="1468"
                        fill="white"
                        transform="translate(0.5 -668)"
                      />
                    </clipPath>
                  </defs>
                </g>
              </svg>
            </div>

            <button
              onClick={() =>
                setSustainabilityPolicyOpen(!sustainabilityPolicyOpen)
              }
              className="relative z-10 w-full p-8 lg:p-[50px] text-left group/accordion cursor-pointer transition-all duration-300 ease-out"
            >
              <div className="flex gap-3 justify-between items-start max-w-[1215px] mx-auto">
                <h3 className="text-[#1A1D21] font-inter text-xl font-medium leading-7 tracking-[-0.72px]">
                  {sustainabilityPageData?.sustainability?.accordion1?.title}
                </h3>
                <div
                  className={`flex min-w-8 h-8 p-1 flex-col justify-center items-center rounded-full border transition-all duration-300 ${
                    sustainabilityPolicyOpen
                      ? "bg-[#1A5852] border-[#1A5852]"
                      : "border-[#C9CDD3]"
                  } group-hover/accordion:bg-[#1A5852] group-hover/accordion:border-[#1A5852] relative`}
                >
                  {/* Horizontal line (always visible) */}
                  <motion.div
                    className={`absolute w-4 h-[2px] rounded-full ${
                      sustainabilityPolicyOpen ? "bg-white" : "bg-[#1A5852]"
                    } group-hover/accordion:bg-white`}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                  />
                  {/* Vertical line (rotates on toggle) */}
                  <motion.div
                    className={`absolute w-[2px] h-4 rounded-full ${
                      sustainabilityPolicyOpen ? "bg-white" : "bg-[#1A5852]"
                    } group-hover/accordion:bg-white`}
                    animate={{
                      rotate: sustainabilityPolicyOpen ? 90 : 0,
                    }}
                    transition={{
                      duration: 0.3,
                      ease: "easeInOut",
                    }}
                  />
                </div>
              </div>
            </button>

            {/* Accordion Content */}
            <motion.div
              initial={false}
              animate={{
                height: sustainabilityPolicyOpen ? "auto" : 0,
              }}
              transition={{
                height: {
                  duration: 0.6,
                  ease: [0.25, 0.1, 0.25, 1],
                },
              }}
              className="overflow-hidden"
            >
              <div className="relative z-10 px-8 lg:px-[50px] pb-8 lg:pb-[50px]">
                <div className="max-w-[1215px] mx-auto">
                  <div className="space-y-8">
                    {/* 1st Accordion description */}
                    {sustainabilityPageData?.sustainability?.accordion1
                      ?.description?.length > 0 && (
                      <RichContent
                        html={
                          sustainabilityPageData?.sustainability?.accordion1
                            ?.description
                        }
                        className="mt-[-18px] text-[#3D434C] text-justify font-inter text-base leading-7"
                      />
                    )}
                    {sustainabilityPageData?.sustainability?.accordion1 && (
                      <Image
                        src={getImageUrl(
                          sustainabilityPageData?.sustainability.accordion1
                            .image.url || ""
                        )}
                        alt={
                          sustainabilityPageData?.sustainability?.accordion1
                            ?.image?.alternativeText || "Sustainability Policy"
                        }
                        width={1215}
                        height={400}
                        className="w-full h-auto object-cover"
                      />
                    )}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Progress Bar */}
            <div className="flex h-[2px] bg-[#F4F5F6] relative">
              <div className="h-[2px] bg-[#1C8579] absolute left-0 top-0 transition-all duration-500 w-0 group-hover:w-full"></div>
            </div>
          </div>

          {/* ID&E Sustainability Management Framework Accordion */}
          <div className="bg-white shadow-[0_30px_60px_0_rgba(0,0,0,0.08)] relative overflow-hidden group">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-30 mix-blend-luminosity pointer-events-none">
              <svg
                width="1317"
                height="132"
                viewBox="0 0 1317 132"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full h-full object-cover"
              >
                <g
                  style={{ mixBlendMode: "luminosity" }}
                  opacity="0.3"
                  clipPath="url(#clip0_3744_3320)"
                >
                  <path d="M3600.5 -668H0.5V932H3600.5V-668Z" fill="#F0F0F0" />
                  <defs>
                    <clipPath id="clip0_3744_3320">
                      <rect
                        width="3600"
                        height="1600"
                        fill="white"
                        transform="translate(0.5 -668)"
                      />
                    </clipPath>
                  </defs>
                </g>
              </svg>
            </div>

            <button
              onClick={() =>
                setManagementFrameworkOpen(!managementFrameworkOpen)
              }
              className="relative z-10 w-full p-8 lg:p-[50px] text-left group/accordion cursor-pointer transition-all duration-300 ease-out"
            >
              <div className="flex gap-3 justify-between items-start max-w-[1215px] mx-auto">
                <h3 className="text-[#1A1D21] font-inter text-xl font-medium leading-7 tracking-[-0.72px]">
                  {sustainabilityPageData?.sustainability?.accordion2?.title}
                </h3>
                <div
                  className={`flex min-w-8 h-8 p-1 flex-col justify-center items-center rounded-full border transition-all duration-300 ${
                    managementFrameworkOpen
                      ? "bg-[#1A5852] border-[#1A5852]"
                      : "border-[#C9CDD3]"
                  } group-hover/accordion:bg-[#1A5852] group-hover/accordion:border-[#1A5852] relative`}
                >
                  {/* Horizontal line (always visible) */}
                  <motion.div
                    className={`absolute w-4 h-[2px] rounded-full ${
                      managementFrameworkOpen ? "bg-white" : "bg-[#1A5852]"
                    } group-hover/accordion:bg-white`}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                  />
                  {/* Vertical line (rotates on toggle) */}
                  <motion.div
                    className={`absolute w-[2px] h-4 rounded-full ${
                      managementFrameworkOpen ? "bg-white" : "bg-[#1A5852]"
                    } group-hover/accordion:bg-white`}
                    animate={{
                      rotate: managementFrameworkOpen ? 90 : 0,
                    }}
                    transition={{
                      duration: 0.3,
                      ease: "easeInOut",
                    }}
                  />
                </div>
              </div>
            </button>

            {/* Accordion Content */}
            <motion.div
              initial={false}
              animate={{
                height: managementFrameworkOpen ? "auto" : 0,
              }}
              transition={{
                height: {
                  duration: 0.6,
                  ease: [0.25, 0.1, 0.25, 1],
                },
                opacity: {
                  duration: 0.5,
                  ease: "easeInOut",
                  delay: managementFrameworkOpen ? 0.15 : 0,
                },
              }}
              className="overflow-hidden"
            >
              <div className="relative z-10 px-8 lg:px-[50px] pb-8 lg:pb-[50px]">
                <div className="max-w-[1215px] mx-auto">
                  <div className="space-y-8">
                    {/* 2nd Accordion description */}
                    {sustainabilityPageData?.sustainability?.accordion2
                      ?.description?.length > 0 && (
                      <RichContent
                        html={
                          sustainabilityPageData?.sustainability?.accordion2
                            ?.description
                        }
                        className="mt-[-18px] text-[#3D434C] text-justify font-inter text-base leading-7"
                      />
                    )}

                    {/* Image Block */}
                    <div className="w-full">
                      {sustainabilityPageData?.sustainability?.accordion2 && (
                        <Image
                          src={getImageUrl(
                            sustainabilityPageData?.sustainability.accordion2
                              .image.url || ""
                          )}
                          alt={
                            sustainabilityPageData?.sustainability?.accordion2
                              ?.image?.alternativeText ||
                            "Sustainability Management Framework"
                          }
                          width={1215}
                          height={300}
                          className="w-full h-auto object-cover"
                        />
                      )}
                    </div>

                    {/* Single Line in Middle of Horizontal Line */}
                    <div className="relative flex items-center justify-center">
                      <div className="px-4 text-[#3D434C] font-inter text-sm font-semibold">
                        {
                          sustainabilityPageData?.sustainability?.accordion2
                            ?.title
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Progress Bar */}
            <div className="flex h-[2px] bg-[#F4F5F6] relative">
              <div className="h-[2px] bg-[#1C8579] absolute left-0 top-0 transition-all duration-500 w-0 group-hover:w-full"></div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* ESG Initiatives Section */}
      <section className="relative py-16 lg:py-[100px]">
        <div className="mx-auto">
          <motion.div
            className="rounded-lg"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={sectionVariants}
          >
            {/* Section Header */}
            <div className="flex flex-col items-center gap-4 mb-[50px]">
              <h2 className="text-[#0A1119] text-center font-inter text-3xl lg:text-[40px] font-medium leading-tight lg:leading-[58px] tracking-[-1.44px]">
                {sustainabilityPageData?.esg?.title}
              </h2>
              <p className="text-[#3D434C] text-center font-inter text-xl font-medium leading-7">
                {sustainabilityPageData?.esg?.subtitle}
              </p>
              <div className="w-[50px] h-[2px] bg-[#1A5852]"></div>
            </div>

            {/* Description and ESG Cards Container */}
            <div className="w-full group/progress">
              <div
                className="relative space-y-11 bg-white shadow-[0_30px_60px_0_rgba(0,0,0,0.08)] rounded-lg p-8 lg:p-12"
                style={{
                  backgroundImage:
                    "url(/images/sustainability/sustainability-section1-bg.svg)",
                }}
              >
                {/* Description */}
                <RichContent
                  html={sustainabilityPageData?.esg?.description}
                  className="mt-[-18px] text-lg md:text-xl text-[#3D434C] text-justify"
                />

                {/* ESG Cards */}
                <motion.div
                  className="grid grid-cols-1 md:grid-cols-2 gap-8 relative overflow-hidden"
                  variants={staggerContainer}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.1 }}
                >
                  {/* Background Pattern Overlay */}
                  <div className="absolute inset-0 opacity-30 mix-blend-luminosity pointer-events-none">
                    <svg
                      width="1317"
                      height="132"
                      viewBox="0 0 1317 132"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-full h-full object-cover"
                    >
                      <g
                        style={{ mixBlendMode: "luminosity" }}
                        opacity="0.3"
                        clipPath="url(#clip0_3744_904)"
                      >
                        <path
                          d="M3303.5 -668H0.5V800H3303.5V-668Z"
                          fill="#F0F0F0"
                        />
                        <defs>
                          <clipPath id="clip0_3744_904">
                            <rect
                              width="3303"
                              height="1468"
                              fill="white"
                              transform="translate(0.5 -668)"
                            />
                          </clipPath>
                        </defs>
                      </g>
                    </svg>
                  </div>
                  {/* Environment Card - Left Column */}
                  <Link
                    href={`/en/sustainability/${
                      sustainabilityPageData?.esg?.sustainabilities?.[0]
                        ?.slug || "#"
                    }`}
                    className="relative h-[314px] md:h-[660px] overflow-hidden rounded-[20px] group cursor-pointer"
                  >
                    {sustainabilityPageData?.esg?.sustainabilities?.[0]
                      .coverImage && (
                      <Image
                        src={getImageUrl(
                          sustainabilityPageData?.esg?.sustainabilities?.[0]
                            .coverImage.url || ""
                        )}
                        alt={
                          sustainabilityPageData?.esg?.sustainabilities?.[0]
                            .coverImage?.alternativeText || ""
                        }
                        fill
                        className="object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                    )}
                    <div className="absolute inset-0 bg-black/60"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <h3 className="text-white font-inter text-2xl font-semibold text-center">
                        {sustainabilityPageData?.esg?.sustainabilities?.[0]
                          ?.title || ""}
                      </h3>
                    </div>
                  </Link>

                  {/* Right Column - Social and Governance */}
                  <div className="flex flex-col gap-8 relative z-10">
                    {/* Social Card */}
                    <Link
                      href={`/en/sustainability/${
                        sustainabilityPageData?.esg?.sustainabilities?.[1]
                          ?.slug || "#"
                      }`}
                      className="relative h-[314px] overflow-hidden rounded-[20px] group cursor-pointer"
                    >
                      {sustainabilityPageData?.esg?.sustainabilities?.[1]
                        ?.coverImage && (
                        <Image
                          src={getImageUrl(
                            sustainabilityPageData?.esg?.sustainabilities?.[1]
                              .coverImage.url || ""
                          )}
                          alt={
                            sustainabilityPageData?.esg?.sustainabilities?.[1]
                              ?.coverImage?.alternativeText || ""
                          }
                          fill
                          className="object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                      )}
                      <div className="absolute inset-0 bg-black/60"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <h3 className="text-white font-inter text-2xl font-semibold text-center">
                          {sustainabilityPageData?.esg?.sustainabilities?.[1]
                            ?.title || ""}
                        </h3>
                      </div>
                    </Link>

                    {/* Governance Card */}
                    <Link
                      href={`/en/sustainability/${
                        sustainabilityPageData?.esg?.sustainabilities?.[2]
                          ?.slug || "#"
                      }`}
                      className="relative h-[314px] overflow-hidden rounded-[20px] group cursor-pointer"
                    >
                      {sustainabilityPageData?.esg?.sustainabilities?.[2]
                        ?.coverImage && (
                        <Image
                          src={getImageUrl(
                            sustainabilityPageData?.esg?.sustainabilities?.[2]
                              .coverImage.url || ""
                          )}
                          alt={
                            sustainabilityPageData?.esg?.sustainabilities?.[2]
                              ?.coverImage?.alternativeText || ""
                          }
                          fill
                          className="object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                      )}
                      <div className="absolute inset-0 bg-black/60"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <h3 className="text-white font-inter text-2xl font-semibold text-center">
                          {
                            sustainabilityPageData?.esg?.sustainabilities?.[2]
                              ?.title
                          }
                        </h3>
                      </div>
                    </Link>
                  </div>
                </motion.div>
              </div>
              {/* Progress bar */}
              <div className="flex h-[2px] bg-gray-25 relative">
                <div className="w-0 h-[2px] bg-[#1C8579] absolute left-0 top-0 group-hover/progress:w-full transition-all duration-500"></div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default SustainabilityPageClient;
